import { Dialog } from '@components/dialog';
import { FormControlLabel } from '@components/formControlLabel';
import { RadioButton } from '@components/radioButton';
import { CustomerMatchCandidate } from '@services/customer';
import { Box, RadioGroup, ThemeProvider, Typography } from '@mui/material';
import { EnteredAddress } from '@models/validation';
import { useComposeTheme } from '@theme/theme';
import { formatAddress } from '@utils/address';
import { CustomerMatchResponse } from '@lio/api-types';
import { Fragment, useLayoutEffect, useMemo, useState } from 'react';

type MatchDelta = CustomerMatchCandidate['deltas'][number];
type HighlightToken = MatchDelta['enteredHighlights'][number];

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  street1: 'Address',
  street2: 'Address Line 2',
  city: 'City',
  state: 'State',
  zipCode: 'Zip Code',
};

const ENTERED_OPTION_VALUE = 'entered';

// Mirrors the backend's PROPOSE_THRESHOLD (lio-portal-api customer-matching.ts) — the matcher
// returns every trigram-blocked row it considered, but only scores at or above this are
// meaningful enough to present as a selectable candidate.
const MEANINGFUL_CANDIDATE_THRESHOLD = 0.8;

interface CustomerMatchModalProps {
  open: boolean;
  onClose: () => void;
  matchResult: CustomerMatchResponse | undefined;
  onSelectCandidate: (candidate: CustomerMatchCandidate) => void;
  onProceedAsEntered: () => void;
  enteredName: string;
  enteredAddress: EnteredAddress | undefined;
  cancelButtonText?: string;
  proceedButtonText?: string;
}

const HighlightedTokens = ({ tokens }: { tokens: HighlightToken[] }) => (
  <>
    {tokens.map((token, index) => (
      <Fragment key={`${token.value}-${index}`}>
        {token.changed ? (
          <Box
            component='mark'
            sx={{ backgroundColor: (theme) => `${theme.palette.warning.light}`, color: 'inherit', px: '2px' }}
          >
            {token.value}
          </Box>
        ) : (
          token.value
        )}
      </Fragment>
    ))}
  </>
);

const titleAndBodyForOutcome = (matchResult: CustomerMatchResponse | undefined): { title: string; body: string } => {
  switch (matchResult?.presentation) {
    case 'perfect':
      return {
        title: 'Matching Customer Found',
        body: 'We found an existing customer that matches the information you entered.',
      };
    case 'multi-candidate':
      return {
        title: 'Multiple Possible Matches',
        body: 'We found multiple existing customers that may match. Review the differences and select which record to use.',
      };
    case 'subtle-diff':
      return {
        title: 'Possible Customer Match',
        body: 'This looks similar to an existing customer. Review the differences below and choose which record to use.',
      };
    default:
      return {
        title: 'Customer Validation',
        body: 'Please select one, or proceed with the information you entered.',
      };
  }
};

export const CustomerMatchModal = ({
  open,
  onClose,
  matchResult,
  onSelectCandidate,
  onProceedAsEntered,
  enteredName,
  enteredAddress,
  cancelButtonText = 'Exit',
  proceedButtonText = 'Use Selected Customer',
}: CustomerMatchModalProps) => {
  const composeTheme = useComposeTheme('light');
  const candidates = useMemo(
    () => (matchResult?.candidates ?? []).filter((candidate) => candidate.score >= MEANINGFUL_CANDIDATE_THRESHOLD),
    [matchResult],
  );
  // The backend resolves every auto-match by returning the existing customer before it ever
  // considers createAsEntered, so "Use as entered" must not be offered — or defaulted to — for
  // an auto-match, regardless of whether it's presented as a clean 'perfect' match or a
  // 'subtle-diff' one with formatting/address deltas.
  const isAutoMatch = matchResult?.outcome === 'auto-match';
  const allowAsEntered = !isAutoMatch;

  const [selectedValue, setSelectedValue] = useState<string>('');

  // This dialog is kept mounted and only toggled via `open`, so its selection must be
  // recomputed for every new match result — otherwise a choice from a previous match
  // attempt in the same panel session can silently carry over into this one.
  useLayoutEffect(() => {
    if (open) {
      setSelectedValue(isAutoMatch && candidates[0] ? candidates[0].id : allowAsEntered ? ENTERED_OPTION_VALUE : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, matchResult]);

  const { title, body } = useMemo(() => titleAndBodyForOutcome(matchResult), [matchResult]);

  const handleProceed = () => {
    if (selectedValue === ENTERED_OPTION_VALUE) {
      onProceedAsEntered();
    } else {
      const selectedCandidate = candidates.find((candidate) => candidate.id === selectedValue);
      if (selectedCandidate) {
        onSelectCandidate(selectedCandidate);
      }
    }
    onClose();
  };

  return (
    <ThemeProvider theme={composeTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        handleCloseDialog={onClose}
        title={title}
        primaryBtnText={proceedButtonText}
        isPrimaryBtnDisabled={!selectedValue}
        secondaryBtnText={cancelButtonText}
        primaryBtnAction={handleProceed}
        secondaryBtnAction={onClose}
        size='small'
      >
        <Box sx={{ padding: '0px 28px' }}>
          <Typography variant='caption' display='block' sx={{ mt: 2, mb: 2 }}>
            {body}
          </Typography>
          <RadioGroup value={selectedValue} onChange={(event) => setSelectedValue(event.target.value)}>
            {candidates.map((candidate) => {
              const differingDeltas = candidate.deltas.filter((delta) => delta.different);
              return (
                <FormControlLabel
                  key={candidate.id}
                  value={candidate.id}
                  control={<RadioButton />}
                  sxProps={{ formControlLabel: { alignItems: 'flex-start', marginBottom: '8px' } }}
                  label={
                    <Box sx={{ pt: '2px' }}>
                      <Typography variant='bodySmall' fontWeight='bold' sx={{ color: 'secondary.main' }}>
                        {candidate.name}
                        <Box component='span' fontWeight='normal' sx={{ color: 'text.primary' }}>
                          {' '}
                          ({candidate.address.street1}
                          {candidate.address.street2 ? `, ${candidate.address.street2}` : ''}, {candidate.address.city},{' '}
                          {candidate.address.state} {candidate.address.zipCode})
                        </Box>
                      </Typography>
                      {differingDeltas.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {differingDeltas.map((delta) => (
                            <Typography variant='caption' display='block' color='text.subdued' key={delta.field}>
                              {FIELD_LABELS[delta.field] || delta.field}: you entered{' '}
                              <HighlightedTokens tokens={delta.enteredHighlights} /> — existing customer has{' '}
                              <HighlightedTokens tokens={delta.candidateHighlights} />
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  }
                />
              );
            })}
            {allowAsEntered && (
              <FormControlLabel
                value={ENTERED_OPTION_VALUE}
                control={<RadioButton />}
                sxProps={{ formControlLabel: { alignItems: 'flex-start' } }}
                label={
                  <Box sx={{ pt: '2px' }}>
                    <Typography variant='bodySmall' fontWeight='bold' sx={{ color: 'secondary.main' }}>
                      {enteredName}
                      <Box component='span' fontWeight='normal' sx={{ color: 'text.primary' }}>
                        {' '}
                        ({enteredAddress ? formatAddress(enteredAddress) : ''})
                      </Box>
                    </Typography>
                    <Typography variant='caption' display='block' color='text.subdued'>
                      Use as entered — creates a new customer record
                    </Typography>
                  </Box>
                }
              />
            )}
          </RadioGroup>
        </Box>
      </Dialog>
    </ThemeProvider>
  );
};
